import React, { useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, TextField,
  FormControl, InputLabel, Select, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip,
  Alert, Divider, LinearProgress
} from '@mui/material';
import {
  TrendingUp as EstimateIcon,
  Schedule as TimeIcon,
  AttachMoney as CostIcon,
  Group as TeamIcon,
  Calculate as CalculateIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';

const ProjectEstimator = ({ tasks, employees }) => {
  const { translate } = useLanguage();
  const [estimates, setEstimates] = useState([]);
  const [newEstimate, setNewEstimate] = useState({
    projectName: '',
    description: '',
    complexity: 'medium',
    teamSize: 1,
    hourlyRate: 75,
    riskFactor: 1.2
  });

  // Calculate estimates based on historical data
  const calculateEstimate = () => {
    const { complexity, teamSize, hourlyRate, riskFactor } = newEstimate;
    
    // Base hours based on complexity
    const baseHours = {
      'simple': 40,
      'medium': 80,
      'complex': 160,
      'very_complex': 320
    };

    // Team efficiency factor (more people = some overhead)
    const teamEfficiency = Math.pow(teamSize, 0.8);

    // Calculate estimated hours
    const estimatedHours = (baseHours[complexity] * riskFactor) / teamEfficiency;
    
    // Calculate estimated cost
    const estimatedCost = estimatedHours * hourlyRate * teamSize;

    // Calculate timeline (assuming 8 hours per day, 5 days per week)
    const workingDays = estimatedHours / (8 * teamSize);
    const workingWeeks = workingDays / 5;

    return {
      hours: Math.round(estimatedHours),
      cost: Math.round(estimatedCost),
      weeks: Math.ceil(workingWeeks),
      days: Math.ceil(workingDays)
    };
  };

  const handleAddEstimate = () => {
    if (newEstimate.projectName && newEstimate.description) {
      const estimate = calculateEstimate();
      const estimateData = {
        id: Date.now(),
        ...newEstimate,
        ...estimate,
        createdAt: new Date().toISOString()
      };
      
      setEstimates([...estimates, estimateData]);
      setNewEstimate({
        projectName: '',
        description: '',
        complexity: 'medium',
        teamSize: 1,
        hourlyRate: 75,
        riskFactor: 1.2
      });
    }
  };

  const handleSaveEstimates = () => {
    // TODO: Save estimates to backend
    console.log('Saving estimates:', estimates);
  };

  // Historical data analysis
  const historicalData = {
    totalTasks: tasks.length,
    completedTasks: tasks.filter(t => t.status === 'completed').length,
    avgEstimatedHours: tasks.length > 0 ? 
      tasks.reduce((sum, t) => sum + t.estimatedHours, 0) / tasks.length : 0,
    avgActualHours: tasks.length > 0 ? 
      tasks.filter(t => t.actualHours > 0).reduce((sum, t) => sum + t.actualHours, 0) / 
      tasks.filter(t => t.actualHours > 0).length : 0,
    avgBudget: tasks.length > 0 ? 
      tasks.reduce((sum, t) => sum + t.budget, 0) / tasks.length : 0,
    avgSpent: tasks.length > 0 ? 
      tasks.filter(t => t.spent > 0).reduce((sum, t) => sum + t.spent, 0) / 
      tasks.filter(t => t.spent > 0).length : 0
  };

  const accuracyRate = historicalData.avgEstimatedHours > 0 ? 
    (historicalData.avgActualHours / historicalData.avgEstimatedHours) * 100 : 0;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const complexityLevels = [
    { value: 'simple', label: 'Simple', description: 'Basic features, well-defined scope' },
    { value: 'medium', label: 'Medium', description: 'Standard features, moderate complexity' },
    { value: 'complex', label: 'Complex', description: 'Advanced features, multiple integrations' },
    { value: 'very_complex', label: 'Very Complex', description: 'Enterprise-level, high risk' }
  ];

  return (
    <Box>
      {/* Historical Data Analysis */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Historical Data Analysis
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {historicalData.totalTasks}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Tasks
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="success.main">
                  {historicalData.completedTasks}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Completed Tasks
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="info.main">
                  {historicalData.avgEstimatedHours.toFixed(1)}h
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg Estimated Hours
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main">
                  {historicalData.avgActualHours.toFixed(1)}h
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Avg Actual Hours
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Estimation Accuracy</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(accuracyRate, 100)}
                  sx={{ flexGrow: 1, height: 10, borderRadius: 5 }}
                  color={accuracyRate > 120 ? 'error' : accuracyRate > 100 ? 'warning' : 'success'}
                />
                <Typography variant="body2">
                  {accuracyRate.toFixed(1)}%
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {accuracyRate > 100 ? 'Underestimated' : 'Overestimated'} on average
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Budget Accuracy</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2">
                  Avg Budget: {formatCurrency(historicalData.avgBudget)}
                </Typography>
                <Typography variant="body2">
                  Avg Spent: {formatCurrency(historicalData.avgSpent)}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {historicalData.avgSpent > historicalData.avgBudget ? 'Over budget' : 'Under budget'} on average
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* New Estimate Form */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Create New Estimate
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Project Name"
                value={newEstimate.projectName}
                onChange={(e) => setNewEstimate({ ...newEstimate, projectName: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Complexity Level</InputLabel>
                <Select
                  value={newEstimate.complexity}
                  onChange={(e) => setNewEstimate({ ...newEstimate, complexity: e.target.value })}
                  label="Complexity Level"
                >
                  {complexityLevels.map(level => (
                    <MenuItem key={level.value} value={level.value}>
                      {level.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Project Description"
                multiline
                rows={3}
                value={newEstimate.description}
                onChange={(e) => setNewEstimate({ ...newEstimate, description: e.target.value })}
                sx={{ mb: 2 }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Team Size"
                type="number"
                value={newEstimate.teamSize}
                onChange={(e) => setNewEstimate({ ...newEstimate, teamSize: parseInt(e.target.value) || 1 })}
                inputProps={{ min: 1, max: 20 }}
                sx={{ mb: 2 }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Hourly Rate ($)"
                type="number"
                value={newEstimate.hourlyRate}
                onChange={(e) => setNewEstimate({ ...newEstimate, hourlyRate: parseFloat(e.target.value) || 0 })}
                inputProps={{ min: 0, step: 0.01 }}
                sx={{ mb: 2 }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Risk Factor"
                type="number"
                value={newEstimate.riskFactor}
                onChange={(e) => setNewEstimate({ ...newEstimate, riskFactor: parseFloat(e.target.value) || 1 })}
                inputProps={{ min: 0.5, max: 3, step: 0.1 }}
                helperText="1.0 = no risk, 1.5 = high risk"
                sx={{ mb: 2 }}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<CalculateIcon />}
                onClick={calculateEstimate}
                sx={{ mb: 2, height: 56 }}
              >
                Calculate
              </Button>
            </Grid>
          </Grid>

          {/* Estimate Preview */}
          {newEstimate.projectName && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Estimate Preview</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="primary">
                      {calculateEstimate().hours}h
                    </Typography>
                    <Typography variant="caption">Estimated Hours</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="success.main">
                      {formatCurrency(calculateEstimate().cost)}
                    </Typography>
                    <Typography variant="caption">Estimated Cost</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="info.main">
                      {calculateEstimate().weeks}w
                    </Typography>
                    <Typography variant="caption">Timeline (Weeks)</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" color="warning.main">
                      {calculateEstimate().days}d
                    </Typography>
                    <Typography variant="caption">Timeline (Days)</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<CalculateIcon />}
              onClick={handleAddEstimate}
              disabled={!newEstimate.projectName || !newEstimate.description}
            >
              Add Estimate
            </Button>
            <Button
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={handleSaveEstimates}
              disabled={estimates.length === 0}
            >
              Save All Estimates
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Estimates Table */}
      {estimates.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Project Estimates
            </Typography>
            
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Project</TableCell>
                    <TableCell>Complexity</TableCell>
                    <TableCell align="right">Team Size</TableCell>
                    <TableCell align="right">Hours</TableCell>
                    <TableCell align="right">Cost</TableCell>
                    <TableCell align="right">Timeline</TableCell>
                    <TableCell align="center">Risk Level</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {estimates.map((estimate) => (
                    <TableRow key={estimate.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {estimate.projectName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {estimate.description}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={complexityLevels.find(c => c.value === estimate.complexity)?.label}
                          size="small"
                          color={
                            estimate.complexity === 'very_complex' ? 'error' :
                            estimate.complexity === 'complex' ? 'warning' :
                            estimate.complexity === 'medium' ? 'primary' : 'success'
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <TeamIcon fontSize="small" sx={{ mr: 0.5 }} />
                          {estimate.teamSize}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <TimeIcon fontSize="small" sx={{ mr: 0.5 }} />
                          {estimate.hours}h
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <CostIcon fontSize="small" sx={{ mr: 0.5 }} />
                          {formatCurrency(estimate.cost)}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        {estimate.weeks} weeks ({estimate.days} days)
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${estimate.riskFactor}x`}
                          size="small"
                          color={estimate.riskFactor > 1.5 ? 'error' : estimate.riskFactor > 1.2 ? 'warning' : 'success'}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Tips and Recommendations */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Estimation Tips
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Historical Accuracy: {accuracyRate.toFixed(1)}%
                </Typography>
                <Typography variant="body2">
                  Based on {historicalData.completedTasks} completed tasks, 
                  estimates are typically {accuracyRate > 100 ? 'underestimated' : 'overestimated'} by 
                  {Math.abs(100 - accuracyRate).toFixed(1)}%.
                </Typography>
              </Alert>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Risk Factors
                </Typography>
                <Typography variant="body2">
                  • New technology: +20%<br/>
                  • Complex integrations: +30%<br/>
                  • Team inexperience: +40%<br/>
                  • Unclear requirements: +50%
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProjectEstimator; 